import { styled } from '@mui/material/styles'
import { CSSProperties, ReactNode, useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { EventType } from '../../../constants'
import { useDebug, useEventTracking, useFormAnalytics, useIsFitterSchedulingMode } from '../../../hooks'
import { AppointmentFormData } from '../../../types'
import { Form as SharedForm } from '../../forms'
import { Container, Loader } from '../../Loader'
import { CallToActions } from '../CallToActions'
import { Content } from './Content'
import { Providers } from './Providers'
import { useIsFormReady } from './useIsFormReady'
import { validationSchema } from './validationSchema'
import { logFactory } from '../../../util'

const log = logFactory('ScheduleForm')

interface Props {
  children?: ReactNode
  defaultValues: AppointmentFormData
  isConfigurationReadOnly?: boolean
  onSubmit: (data: any) => void
}

const LoaderContainer = styled(Container)`
  top: 1.25rem;
  right: 1.25rem;
`

const FormStatus = () => {
  const { formState } = useFormContext<AppointmentFormData>()
  const { isSubmitting, isValidating } = formState
  const isLoading = isSubmitting || isValidating

  if (!isLoading) return null

  return (
    <LoaderContainer>
      <Loader />
    </LoaderContainer>
  )
}

const Form = ({ children, defaultValues, onSubmit }: Props) => {
  const debug = useDebug()
  const { isReady: isFormReady } = useIsFormReady()
  const isFitterSchedulingMode = useIsFitterSchedulingMode()
  const { track: trackEvent } = useEventTracking()
  const { onErrorsChange } = useFormAnalytics<AppointmentFormData>()

  useEffect(() => {
    trackEvent(EventType.APPOINTMENT_FORM_VIEWED)
  }, [])

  const onError = useCallback(
    (errors) => {
      log('🔴 Form validation errors:', errors)
      onErrorsChange(errors)
    },
    [onErrorsChange]
  )

  const ctaHeight = '3.5rem'

  const style: CSSProperties = isFitterSchedulingMode
    ? {
        alignItems: 'center',
        background: '#fff',
        bottom: 0,
        display: 'grid',
        height: ctaHeight,
        left: 0,
        position: 'fixed',
        right: 0,
      }
    : { width: '100%' }

  return (
    <SharedForm<AppointmentFormData>
      defaultValues={defaultValues}
      disabled={!isFormReady}
      onError={onError}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <FormStatus />
      <Content debug={debug} />
      {isFitterSchedulingMode ? <div style={{ height: ctaHeight }} /> : <div style={{ height: '0.5rem' }} />}
      <div style={style}>
        <CallToActions>{children}</CallToActions>
      </div>
    </SharedForm>
  )
}

const Schedule = ({ children, defaultValues, isConfigurationReadOnly, onSubmit }: Props) => (
  <Providers isConfigurationReadOnly={isConfigurationReadOnly}>
    <Form defaultValues={defaultValues} onSubmit={onSubmit}>
      {children}
    </Form>
  </Providers>
)

export { Schedule }
